import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {MessageService} from 'primeng/api';
import {Subscription} from 'rxjs';
import {
    AssignmentStatistics,
    CampaignMemberAssignmentService
} from '../../service/api/campaign-member-assignment.service';
import {CampaignMemberDto} from '../../model/dto/entity/campaign-member.dto';
import {CampaignMemberAssignmentHistoryDto} from '../../model/dto/entity/campaign-member-assignment-history.dto';
import {AssignmentMethodEnum} from '../../enum/assignment-method.enum';

@Component({
    selector: 'ct-campaign-member-assignment',
    templateUrl: './campaign-member-assignment.component.html',
    styleUrls: ['./campaign-member-assignment.component.scss'],
    standalone: false
})
export class CampaignMemberAssignmentComponent implements OnInit, OnDestroy {

    @Input() campaignId: number;
    @Input() members: CampaignMemberDto[] = [];
    @Input() agents: { id: number, name: string }[] = [];

    @Output() onAssignmentChange = new EventEmitter<CampaignMemberDto[]>();

    // State
    statistics: AssignmentStatistics;
    unassignedMembers: CampaignMemberDto[] = [];
    selectedMembers: CampaignMemberDto[] = [];
    selectedAgent: { id: number, name: string };
    assignmentReason: string = '';
    assignmentHistory: CampaignMemberAssignmentHistoryDto[] = [];
    selectedMemberForHistory: CampaignMemberDto;

    // Dialog states
    showBulkAssignDialog: boolean = false;
    showReassignDialog: boolean = false;
    showHistoryDialog: boolean = false;

    // Reassign dialog
    fromAgent: { id: number, name: string };
    toAgent: { id: number, name: string };
    reassignReason: string = '';

    // UI State
    busy: boolean = false;

    // Enums for template
    AssignmentMethodEnum = AssignmentMethodEnum;

    private subscriptions: Subscription[] = [];

    constructor(
        private assignmentService: CampaignMemberAssignmentService,
        private messageService: MessageService
    ) {
    }

    ngOnInit(): void {
        this.loadStatistics();
        this.loadUnassignedMembers();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    loadStatistics(): void {
        if (!this.campaignId) return;

        this.busy = true;
        const sub = this.assignmentService.getAssignmentStatistics(this.campaignId).subscribe({
            next: (stats) => {
                this.statistics = stats;
                this.busy = false;
            },
            error: (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Fehler',
                    detail: 'Zuweisungsstatistiken konnten nicht geladen werden'
                });
                this.busy = false;
            }
        });
        this.subscriptions.push(sub);
    }

    loadUnassignedMembers(): void {
        if (!this.campaignId) return;

        this.busy = true;
        const sub = this.assignmentService.getUnassignedMembers(this.campaignId).subscribe({
            next: (members) => {
                this.unassignedMembers = members;
                this.busy = false;
            },
            error: (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Fehler',
                    detail: 'Nicht zugewiesene Mitglieder konnten nicht geladen werden'
                });
                this.busy = false;
            }
        });
        this.subscriptions.push(sub);
    }

    assignSingleMember(member: CampaignMemberDto, agentId: number, reason?: string): void {
        this.busy = true;
        const sub = this.assignmentService.assignMember(member.id, { agentId, reason }).subscribe({
            next: (updated) => {
                this.handleMemberUpdate(updated);
                this.messageService.add({
                    severity: 'success',
                    summary: 'Zugewiesen',
                    detail: `Mitglied erfolgreich dem Agenten zugewiesen`
                });
                this.busy = false;
            },
            error: (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Fehler',
                    detail: 'Mitglied konnte nicht zugewiesen werden'
                });
                this.busy = false;
            }
        });
        this.subscriptions.push(sub);
    }

    unassignMember(member: CampaignMemberDto, reason?: string): void {
        this.busy = true;
        const sub = this.assignmentService.unassignMember(member.id, reason).subscribe({
            next: (updated) => {
                this.handleMemberUpdate(updated);
                this.messageService.add({
                    severity: 'success',
                    summary: 'Zuweisung aufgehoben',
                    detail: `Mitglied erfolgreich abgemeldet`
                });
                this.busy = false;
            },
            error: (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Fehler',
                    detail: 'Zuweisung des Mitglieds konnte nicht aufgehoben werden'
                });
                this.busy = false;
            }
        });
        this.subscriptions.push(sub);
    }

    openBulkAssignDialog(): void {
        if (this.selectedMembers.length === 0) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Keine Auswahl',
                detail: 'Bitte wählen Sie Mitglieder zum Zuweisen aus'
            });
            return;
        }
        this.showBulkAssignDialog = true;
    }

    executeBulkAssign(): void {
        if (!this.selectedAgent) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Kein Agent ausgewählt',
                detail: 'Bitte wählen Sie einen Agenten für die Zuweisung aus'
            });
            return;
        }

        this.busy = true;
        const memberIds = this.selectedMembers.map(m => m.id);
        const sub = this.assignmentService.bulkAssign({
            memberIds,
            agentId: this.selectedAgent.id,
            reason: this.assignmentReason
        }).subscribe({
            next: (response) => {
                response.members.forEach(m => this.handleMemberUpdate(m));
                this.selectedMembers = [];
                this.showBulkAssignDialog = false;
                this.assignmentReason = '';
                this.selectedAgent = null;
                this.messageService.add({
                    severity: 'success',
                    summary: 'Massenzuweisung abgeschlossen',
                    detail: `${response.assignedCount} Mitglieder erfolgreich zugewiesen`
                });
                this.busy = false;
            },
            error: (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Fehler',
                    detail: 'Massenzuweisung der Mitglieder fehlgeschlagen'
                });
                this.busy = false;
            }
        });
        this.subscriptions.push(sub);
    }

    openReassignDialog(): void {
        this.showReassignDialog = true;
    }

    executeReassign(): void {
        if (!this.fromAgent || !this.toAgent) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Fehlende Auswahl',
                detail: 'Bitte wählen Sie sowohl Quell- als auch Zielagenten aus'
            });
            return;
        }

        if (this.fromAgent.id === this.toAgent.id) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Ungültige Auswahl',
                detail: 'Quell- und Zielagent müssen unterschiedlich sein'
            });
            return;
        }

        this.busy = true;
        const sub = this.assignmentService.reassignMembers(this.campaignId, {
            fromAgentId: this.fromAgent.id,
            toAgentId: this.toAgent.id,
            reason: this.reassignReason
        }).subscribe({
            next: (response) => {
                this.showReassignDialog = false;
                this.fromAgent = null;
                this.toAgent = null;
                this.reassignReason = '';
                this.loadStatistics();
                this.loadUnassignedMembers();
                this.onAssignmentChange.emit([]);
                this.messageService.add({
                    severity: 'success',
                    summary: 'Umverteilt',
                    detail: `${response.reassignedCount} Mitglieder erfolgreich umverteilt`
                });
                this.busy = false;
            },
            error: (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Fehler',
                    detail: 'Umverteilung der Mitglieder fehlgeschlagen'
                });
                this.busy = false;
            }
        });
        this.subscriptions.push(sub);
    }

    executeAutoAssignment(): void {
        this.busy = true;
        const sub = this.assignmentService.executeAutoAssignment(this.campaignId).subscribe({
            next: (response) => {
                this.loadStatistics();
                this.loadUnassignedMembers();
                this.onAssignmentChange.emit([]);
                this.messageService.add({
                    severity: 'success',
                    summary: 'Automatische Zuweisung abgeschlossen',
                    detail: `${response.assignedCount} Mitglieder automatisch zugewiesen`
                });
                this.busy = false;
            },
            error: (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Fehler',
                    detail: 'Automatische Zuweisung konnte nicht ausgeführt werden'
                });
                this.busy = false;
            }
        });
        this.subscriptions.push(sub);
    }

    showMemberHistory(member: CampaignMemberDto): void {
        this.selectedMemberForHistory = member;
        this.busy = true;
        const sub = this.assignmentService.getAssignmentHistory(member.id).subscribe({
            next: (history) => {
                this.assignmentHistory = history;
                this.showHistoryDialog = true;
                this.busy = false;
            },
            error: (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Fehler',
                    detail: 'Zuweisungshistorie konnte nicht geladen werden'
                });
                this.busy = false;
            }
        });
        this.subscriptions.push(sub);
    }

    private handleMemberUpdate(updated: CampaignMemberDto): void {
        // Update in members list
        const index = this.members.findIndex(m => m.id === updated.id);
        if (index !== -1) {
            this.members[index] = updated;
        }

        // Update unassigned list
        if (updated.assignedAgentDtoId) {
            this.unassignedMembers = this.unassignedMembers.filter(m => m.id !== updated.id);
        } else {
            const unassignedIndex = this.unassignedMembers.findIndex(m => m.id === updated.id);
            if (unassignedIndex === -1) {
                this.unassignedMembers.push(updated);
            }
        }

        this.loadStatistics();
        this.onAssignmentChange.emit([updated]);
    }

    getAssignmentMethodLabel(method: AssignmentMethodEnum): string {
        const labels: { [key: number]: string } = {
            [AssignmentMethodEnum.manual]: 'Manuell',
            [AssignmentMethodEnum.roundRobin]: 'Rundlauf',
            [AssignmentMethodEnum.territoryBased]: 'Gebietsbasiert',
            [AssignmentMethodEnum.workloadBalanced]: 'Arbeitslastverteilt',
            [AssignmentMethodEnum.bulkReassign]: 'Massenumverteilung'
        };
        return labels[method] || String(method);
    }

    getAssignedPercentage(): number {
        if (!this.statistics || this.statistics.total === 0) return 0;
        return Math.round((this.statistics.assigned / this.statistics.total) * 100);
    }
}
